import { useCallback, useState } from 'react';
import { toast } from '../utils/toast';
import networkErrorHandler from '../utils/networkErrorHandler';

/**
 * Custom hook for standardized error handling
 */
export const useErrorHandler = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleError = useCallback((error, options = {}) => {
    console.error('Error handled by useErrorHandler:', error);
    
    // Set error state
    setError(error);
    
    // Get user-friendly message
    const message = networkErrorHandler.getUserFriendlyMessage(error);
    
    // Show toast notification
    if (options.showToast !== false) {
      toast.error(message);
    }
    
    // Get recovery suggestions
    const suggestions = networkErrorHandler.getRecoverySuggestions(error);
    
    // Log error for debugging
    if (process.env.NODE_ENV === 'development') {
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        suggestions
      });
    }
    
    return {
      message,
      suggestions,
      error
    };
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const executeWithErrorHandling = useCallback(async (asyncFn, options = {}) => {
    try {
      setIsLoading(true);
      clearError();
      
      const result = await asyncFn();
      return result;
    } catch (error) {
      const errorInfo = handleError(error, options);
      throw errorInfo;
    } finally {
      setIsLoading(false);
    }
  }, [handleError, clearError]);

  return {
    error,
    isLoading,
    handleError,
    clearError,
    executeWithErrorHandling
  };
};

export default useErrorHandler;
